import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProduct } from "../utils/api";
import { formatDate, formatCurrency, warrantyStatus } from "../utils/helpers";

export default function WarrantyPublicPage() {
  const { uniqueId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getProduct(uniqueId);
        setProduct(res.data.data);
      } catch {
        setError("Record not found. The ID may be invalid.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [uniqueId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0f0f0f" }}>
        <div className="text-center">
          <div className="w-12 h-12 rounded-full animate-spin mx-auto mb-4" style={{ border: "4px solid #3a2a00", borderTopColor: "#FFD700" }} />
          <p style={{ color: "#8A6000" }}>Loading details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0f0f0f" }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(127,29,29,0.3)", border: "1px solid #7f1d1d" }}>
            <svg className="w-8 h-8" style={{ color: "#cf6f6f" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#f5f0e8", fontFamily: "serif" }}>Not Found</h2>
          <p className="text-sm" style={{ color: "#8A6000" }}>{error}</p>
        </div>
      </div>
    );
  }

  const { status } = warrantyStatus(product.purchaseDate, product.warrantyDurationMonths);
  const isActive = status === "Active";

  return (
    <div className="min-h-screen" style={{ background: "#0f0f0f" }}>
      {/* Navbar */}
      <nav style={{ background: "linear-gradient(90deg, #0a0a0a, #181818, #0a0a0a)", borderBottom: "1px solid rgba(200,150,12,0.3)" }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/makalu-logo.svg" alt="Makalu" style={{ height: 38, width: "auto" }} />
            <div>
              <div style={{ fontSize: 9, color: "#C8960C", letterSpacing: "0.15em", fontWeight: 700 }}>AGRO AND TRADE</div>
              <div style={{ fontSize: 8, color: "#8A6000", letterSpacing: "0.1em" }}>SACRED HIMALAYAN RUDRAKSHA</div>
            </div>
          </div>
          <span className="px-3 py-1.5 rounded-full text-xs font-bold" style={
            isActive
              ? { background: "rgba(74,124,78,0.2)", color: "#6fcf7f", border: "1px solid rgba(74,124,78,0.5)" }
              : { background: "rgba(124,74,74,0.2)", color: "#cf6f6f", border: "1px solid rgba(124,74,74,0.5)" }
          }>
            {isActive ? "✓ CERTIFIED AUTHENTIC" : "✗ RECORD EXPIRED"}
          </span>
        </div>
        <div style={{ height: 2, background: "linear-gradient(90deg, transparent, #8A6000, #FFD700, #C8960C, #FFD700, #8A6000, transparent)" }} />
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">

        {/* Status Banner */}
        <div className="rounded-2xl p-5" style={
          isActive
            ? { background: "rgba(74,124,78,0.1)", border: "1px solid rgba(74,124,78,0.3)" }
            : { background: "rgba(124,74,74,0.1)", border: "1px solid rgba(124,74,74,0.3)" }
        }>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={
              isActive ? { background: "rgba(74,124,78,0.3)", border: "1px solid rgba(74,124,78,0.6)" }
                       : { background: "rgba(124,74,74,0.3)", border: "1px solid rgba(124,74,74,0.6)" }
            }>
              {isActive ? (
                <svg className="w-6 h-6" style={{ color: "#6fcf7f" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" style={{ color: "#cf6f6f" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div>
              <p className="font-bold text-lg" style={{ color: "#f5f0e8", fontFamily: "serif" }}>
                {isActive ? "Authentic & Verified" : "Record Expired"}
              </p>
              <p className="text-sm" style={{ color: "#a09070" }}>
                {isActive ? "This Rudraksha is certified authentic by Makalu Agro and Trade" : "This certificate has expired"}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs" style={{ color: "#8A6000", letterSpacing: "0.08em" }}>CARD UNIQUE ID</p>
              <p className="font-mono font-bold" style={{ color: "#FFD700" }}>{product.warrantyId}</p>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        {product.images?.length > 0 && (
          <div className="rounded-2xl p-4" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)" }}>
            <img
              src={product.images[activeImage].url}
              alt={product.productName}
              className="w-full object-contain rounded-xl mb-3"
              style={{ maxHeight: 280, background: "#111" }}
            />
            {product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {product.images.map((img, i) => (
                  <button key={img.filename} onClick={() => setActiveImage(i)}>
                    <img src={img.url} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                      style={{ border: `2px solid ${i === activeImage ? "#FFD700" : "transparent"}` }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Product Details */}
        <div className="rounded-2xl p-6" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)" }}>
          <h2 className="text-xl font-bold mb-1" style={{ color: "#FFD700", fontFamily: "serif" }}>{product.productName}</h2>
          <p className="text-lg font-bold mb-5" style={{ color: "#C8960C" }}>{formatCurrency(product.productPrice)}</p>

          <div style={{ height: 1, background: "linear-gradient(90deg, transparent, #C8960C, transparent)", marginBottom: 20 }} />

          <div className="grid grid-cols-2 gap-5 text-sm">
            {[
              ["Customer Name", product.customerName],
              ["Phone Number", product.phoneNumber],
              ["Purchase Date", formatDate(product.purchaseDate)],
              ...(product.rudrakshaSize ? [["Size / Mukhi", product.rudrakshaSize]] : []),
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "#8A6000" }}>{label}</p>
                <p className="font-semibold" style={{ color: "#f5f0e8" }}>{value}</p>
              </div>
            ))}
            <div className="col-span-2">
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "#8A6000" }}>Address</p>
              <p className="font-semibold" style={{ color: "#f5f0e8" }}>{product.address}</p>
            </div>
            {product.productDescription && (
              <div className="col-span-2">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "#8A6000" }}>Description</p>
                <p style={{ color: "#a09070", lineHeight: 1.6 }}>{product.productDescription}</p>
              </div>
            )}
          </div>
        </div>

        {/* Origin */}
        <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)" }}>
          <div>
            <p className="text-xs uppercase tracking-widest" style={{ color: "#8A6000" }}>Origin</p>
            <p className="text-3xl font-black" style={{ color: "#FFD700", fontFamily: "serif", letterSpacing: "0.08em" }}>NEPAL</p>
            <p className="text-xs" style={{ color: "#8A6000" }}>Sacred Himalayan Rudraksha</p>
          </div>
          <div className="ml-auto">
            <img src="/makalu-icon.svg" alt="" style={{ width: 48, height: 48, opacity: 0.7 }} />
          </div>
        </div>

        {/* Videos */}
        {product.videos?.length > 0 && (
          <div className="rounded-2xl p-6" style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)" }}>
            <h3 className="font-bold mb-4" style={{ color: "#FFD700", fontFamily: "serif" }}>Product Videos</h3>
            <div className="space-y-3">
              {product.videos.map((v) => (
                <video key={v.filename} src={v.url} controls className="w-full rounded-xl" style={{ background: "#0a0a0a" }} />
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs pb-4" style={{ color: "#5a4a30" }}>
          Verified by <span style={{ color: "#C8960C", fontWeight: 600 }}>Makalu Agro and Trade</span> — Sacred Himalayan Rudraksha
        </p>
      </div>
    </div>
  );
}
